/**
 * Connection API Module
 * Handles all API calls related to connection management
 */

import { ConnectionStatusResponse, RefreshResponse, DeployResponse, ConnectionCache } from './types';
import { trackApiCall } from '../../utils/debugUtils';
import { API_ENDPOINTS } from '../../services/api';

// Cache for connection status with TTL
let connectionCache: ConnectionCache | null = null;

// Global mutex to prevent concurrent connection checks
let connectionCheckInProgress: Promise<any> | null = null;

// API functions for connection management (Phase 2 implementation)
export const connectionApi = {
  checkStatus: async (forceRefresh = false): Promise<ConnectionStatusResponse> => {
    // Prevent concurrent calls unless force refresh
    if (!forceRefresh && connectionCheckInProgress) {
      try {
        return await connectionCheckInProgress;
      } catch (error) {
        // If the in-progress call failed, allow this call to proceed
        connectionCheckInProgress = null;
      }
    }
    
    // Check cache first (5 second TTL for normal checks, ignore cache on force refresh)
    const now = Date.now();
    if (!forceRefresh && connectionCache && (now - connectionCache.timestamp) < connectionCache.ttl) {
      return connectionCache.data;
    }
    
    const checkPromise = (async () => {
      try {
        // Track API call for debugging
        trackApiCall(API_ENDPOINTS.connection.status);

        const headers: HeadersInit = {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        };

        const response = await fetch(`${import.meta.env.VITE_API_URL}${API_ENDPOINTS.connection.status}`, {
          headers
        });
      
        if (!response.ok) {
          // Invalidate cache on error
          connectionCache = null;
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      
        const data = await response.json();

        // Map API response to store format
        let result;
        if (data.success && data.data) {
          const statusData = data.data;

          // Handle all backend statuses correctly
          let mappedStatus: 'connected' | 'connecting' | 'not_connected';
          if (statusData.status === 'connected') {
            mappedStatus = 'connected';
          } else if (statusData.status === 'pending' || statusData.status === 'connecting') {
            mappedStatus = 'connecting';
          } else {
            // Map both 'not_registered' and 'disconnected' to 'not_connected'
            mappedStatus = 'not_connected';
          }
        
          result = {
            status: mappedStatus,
            lastConnected: statusData.last_heartbeat ? new Date(statusData.last_heartbeat) : null,
            deviceName: statusData.pc_info?.hostname || null,
            tallyVersion: statusData.tally_connected ? 'Tally Connected' : null,
            companyName: null, // Will be added when TC provides this info
            connectionId: statusData.tenant_id
          };
        } else {
          // Don't provide fallback data - let the error propagate
          throw new Error('Invalid response format from backend');
        }
      
        // Cache the result with appropriate TTL
        const ttl = result.status === 'connected' ? 5000 : 2000; // 5s for connected, 2s for not_connected
        connectionCache = {
          data: result,
          timestamp: now,
          ttl
        };
      
        return result;
      } catch (error) {
        console.error('Connection status check failed:', error);
        // Invalidate cache on error
        connectionCache = null;
        
        // Rethrow the error instead of masking it
        throw error;
      }
    })();
    
    // Store the promise to prevent concurrent calls
    if (!forceRefresh) {
      connectionCheckInProgress = checkPromise;
      
      // Clear the promise after completion
      checkPromise.finally(() => {
        if (connectionCheckInProgress === checkPromise) {
          connectionCheckInProgress = null;
        }
      });
    }
    
    return checkPromise;
  },
  
  // Helper to invalidate cache
  invalidateCache: () => {
    connectionCache = null;
  },
  
  deploy: async (): Promise<DeployResponse> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${API_ENDPOINTS.connection.deploy}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to deploy connector');
    }
    
    const data = await response.json();
    
    return {
      downloadUrl: data.data.download_url
    };
  },
  
  disconnect: async (): Promise<void> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${API_ENDPOINTS.connection.disconnect}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Disconnect failed: HTTP ${response.status}`);
    }
  },

  refresh: async (): Promise<RefreshResponse> => {
    // Track API call for debugging
    trackApiCall(API_ENDPOINTS.connection.fetchCompanies);

    const response = await fetch(`${import.meta.env.VITE_API_URL}${API_ENDPOINTS.connection.fetchCompanies}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({ force_refresh: true })
    });
    
    if (!response.ok) {
      throw new Error(`Refresh failed: HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Refresh operation failed');
    }
    
    return {
      companies: data.data.companies || [],
      connectionStatus: data.data.connection_status,
      message: data.data.message
    };
  }
};